import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Button } from 'react-bootstrap';
import moment from 'moment';
import { authenticatedFetch } from './authenticatedFetch';
import config from './config';

// Utility functions
const formatDate = (dateString) => moment(dateString).format('YYYY-MM-DD HH:mm:ss');

const getStatusVariant = (status) => {
    switch (status) {
        case 'waiting': return 'warning';
        case 'queued': return 'primary';
        case 'in_progress': return 'success';
        default: return 'secondary';
    }
};

const truncateText = (text, maxLength) => {
    return text && text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

// This component will be rendered in the new window
const DeploymentStatusWindow = ({ initialDeployments, onClose }) => {
    const [deployments, setDeployments] = useState(initialDeployments || []);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchUpdates = async () => {
        setIsLoading(true);
        try {
            const response = await authenticatedFetch(`${config.apiUrl}/github/deployments/active`);
            if (response.ok) {
                const data = await response.json();
                setDeployments(data || []);
                setError(null);
            } else {
                throw new Error('Failed to fetch deployment updates');
            }
        } catch (error) {
            console.error('Error fetching deployment updates:', error);
            setError('Failed to fetch deployment updates. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUpdates(); // Fetch immediately on mount
        const interval = setInterval(fetchUpdates, 10000); // Then poll every 30 seconds

        return () => clearInterval(interval);
    }, []);

    const handleClose = () => {
        if (typeof onClose === 'function') {
            onClose();
        }
        window.close();
    };

    const handleRefresh = () => {
        fetchUpdates();
    };

    if (error) {
        return (
            <div className="p-3">
                <h2>Error</h2>
                <p className="text-danger">{error}</p>
                <Button variant="primary" onClick={handleRefresh} disabled={isLoading}>
                    {isLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
                <Button variant="secondary" onClick={handleClose} className="ms-2">Close</Button>
            </div>
        );
    }

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Active GitHub Deployments</h2>
                <Button variant="primary" onClick={handleRefresh} disabled={isLoading}>
                    {isLoading ? 'Refreshing...' : 'Refresh'}
                </Button>
            </div>
            {!deployments || deployments.length === 0 ? (
                <p>No active deployments found.</p>
            ) : (
                <table className="table table-striped table-bordered table-hover">
                    <thead>
                    <tr>
                        <th>Repo</th>
                        <th>Workflow</th>
                        <th>Status</th>
                        <th>Branch</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {deployments.map((deployment) => (
                        <tr key={deployment.workflow_run_id}>
                            <td title={deployment.repo}>{truncateText(deployment.repo, 30)}</td>
                            <td>{deployment.name}</td>
                            <td>
                  <span className={`badge bg-${getStatusVariant(deployment.status)}`}>
                    {deployment.status.toUpperCase()}
                  </span>
                                <br />
                                <small className="text-muted" title={formatDate(deployment.created_at)}>
                                    {moment(deployment.created_at).fromNow()}
                                </small>
                            </td>
                            <td>{deployment.head_branch}</td>
                            <td>
                                <a
                                    href={deployment.html_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-primary"
                                >
                                    View on GitHub
                                </a>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            )}
            <Button variant="secondary" onClick={handleClose}>Close</Button>
        </div>
    );
};

const GitHubDeploymentStatus = () => {
    const [windowRef, setWindowRef] = useState(null);
    const [deployments, setDeployments] = useState([]);
    const [error, setError] = useState(null);

    const fetchDeployments = useCallback(async () => {
        try {
            const response = await authenticatedFetch(`${config.apiUrl}/github/deployments/active`);
            const data = await response.json();
            console.log('Fetched deployments:', data);
            setDeployments(data || []);
            setError(null);
        } catch (error) {
            console.error('Error fetching deployment data:', error);
            setError('Failed to fetch deployment data');
        }
    }, []);

    useEffect(() => {
        fetchDeployments();
        const interval = setInterval(fetchDeployments, 30000); // Poll every 30 seconds
        return () => clearInterval(interval);
    }, [fetchDeployments]);

    const closeWindow = useCallback(() => {
        if (windowRef) {
            windowRef.close();
            setWindowRef(null);
        }
    }, [windowRef]);

    const openWindow = useCallback(() => {
        if (windowRef && !windowRef.closed) {
            windowRef.focus();
        } else {
            const newWindow = window.open('', '', 'width=800,height=600');
            setWindowRef(newWindow);

            newWindow.document.title = 'GitHub Deployment Status';
            newWindow.document.body.innerHTML = '<div id="root">Loading...</div>';

            const link = newWindow.document.createElement('link');
            link.href = 'https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css';
            link.rel = 'stylesheet';
            newWindow.document.head.appendChild(link);

            ReactDOM.render(
                <DeploymentStatusWindow initialDeployments={deployments} onClose={closeWindow} />,
                newWindow.document.getElementById('root')
            );

            newWindow.addEventListener('beforeunload', () => {
                setWindowRef(null);
            });
        }
    }, [windowRef, closeWindow, deployments]);

    const getIconColor = () => {
        if (deployments && deployments.some(dep => dep.status === 'waiting')) return 'warning';
        if (deployments && deployments.some(dep => dep.status === 'queued')) return 'primary';
        if (deployments && deployments.some(dep => dep.status === 'in_progress')) return 'success';
        return 'secondary';
    };

    return (
        <Button
            onClick={openWindow}
            variant={getIconColor()}
            className="rounded-circle p-1 me-2"
            style={{ marginRight: '10px' }}
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
        </Button>
    );
};

export default GitHubDeploymentStatus;